<script setup lang="ts">
import { SeatType } from '@/api/generated/api.enum';
import UserListItem from '@/core/list-items/UserListItem.vue';
import { useBillingState } from '@/composables/billing/useBillingState';
import SearchSelect from '@/core/selects/SearchSelect.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import { IGenericUser, IUserWithPermissions } from '@/interfaces';
import { matchesAllSearchWords } from 'common/utils/string.utils';
import { computed, toRefs } from 'vue';
import InviteUserListItem from '../list-items/InviteUserListItem.vue';
import { templateRef } from '@vueuse/core';
import { isValidEmail } from 'common/utils/validation.utils';

const props = defineProps<{
  userOptions: IGenericUser[];
  selectedUsers?: IUserWithPermissions[];
  label?: string;
  placeholder: string;
  disabled?: boolean;
  allowInlineAdd?: boolean;
  serverSideSearch?: boolean;
  showResultsOnlyOnQuery?: boolean;
  autocomplete?: string;
}>();
const { userOptions, selectedUsers, serverSideSearch } = toRefs(props);
const { hasSeatsAvailable } = useBillingState();
const searchSelectRef = templateRef('searchSelectRef');

const emit = defineEmits<{
  'user-selected': [value: IGenericUser | null];
  'update:query': [value: string];
}>();

const filterUsers = (data: IGenericUser[], query: string): IGenericUser[] => {
  if (!query) return data;
  return data.filter((user) => {
    return matchesAllSearchWords([user.firstName ?? '', user.lastName ?? '', user.email ?? ''], query);
  });
};

const handleUserSelected = (user: IGenericUser | null | 'all'): void => {
  if (user === 'all') return;

  searchSelectRef.value?.reset();
  emit('user-selected', user);
};

const handleAddByEmail = async (email: string): Promise<void> => {
  handleUserSelected({ email, isPendingInvitation: true } as IGenericUser);
};

const alreadyAddedUser = (query: string): boolean => {
  const email = query.trim().toLowerCase();
  if (!email) return false;

  return selectedUsers.value?.some((user) => user.user.email.trim().toLowerCase() === email) ?? false;
};

const isAllowedToAddInlineUser = computed(
  () =>
    (query: string, results: IGenericUser[]): boolean =>
      !!props.allowInlineAdd && !results.length && isValidEmail(query) && !alreadyAddedUser(query),
);

const handleEnterPressed = async (query: string, results: IGenericUser[]): Promise<void> => {
  if (!hasSeatsAvailable(SeatType.user)) return;
  if (!isAllowedToAddInlineUser.value(query, results)) return;
  await handleAddByEmail(query);
};

const handleQueryUpdate = (query: string): void => {
  emit('update:query', query);
};
</script>

<template>
  <SearchSelect
    ref="searchSelectRef"
    :options="userOptions"
    :placeholder="placeholder"
    :label="label ? label : placeholder"
    :filter-callback="serverSideSearch ? undefined : filterUsers"
    :disabled="disabled"
    :show-results-only-on-query="showResultsOnlyOnQuery"
    :autocomplete="autocomplete"
    @enter-pressed="handleEnterPressed"
    @option-selected="handleUserSelected"
    @update:query="handleQueryUpdate"
  >
    <template #default="{ option, active }">
      <UserListItem
        :user="option"
        type="add"
        class="bg-white"
        :class="{
          '!bg-primary/20': active,
        }"
      />
    </template>
    <template #emptyPlaceholder="{ query, results }">
      <InviteUserListItem
        v-if="isAllowedToAddInlineUser(query, results)"
        :email="query"
        type="add"
        :disabled="disabled"
        class="bg-white"
        @click:add="handleAddByEmail(query)"
      />
      <EmptyPlaceholder v-else :data="results" :message="$t('general.noResults')" />
    </template>
  </SearchSelect>
</template>
